import { RenderItemProps } from '@shared/components/ui/VirtualList';
import { faCheckCircle, faCircleXmark, faGripVertical } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from '@shared/components/ui/badge/Badge';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { RulesModel } from '../../models/Rules';
import { DropdownActions } from '../interaction/DropdownActions';
import { DragHandle } from './DragHandle';
import { RuleListSummary } from './RuleListSummary';

// 🎯 Props para Card incluyendo las acciones
export interface CardProps extends RenderItemProps<RulesModel> {
  className?: string;
  showIndex?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  dragHandleProps?: any;
  isActive?: boolean;
  onToggleActive?: () => void;
  isReadOnly?: boolean; // 🎯 Flag para modo solo lectura
  canEdit?: boolean;
  canDelete?: boolean;
  /** Listado con drag activo: vista compacta (p. ej. reglas de cliente) */
  isListReordering?: boolean;
  /** Elemento bajo el cursor en DragOverlay */
  isDragPreview?: boolean;
}

// 🎯 Componente de card individual para Regla - SRP
// Card.tsx
// Componente individual de tarjeta para mostrar una regla de envío en la vista de CardList.
export const Card = ({
  item: rules,
  className = '',
  onEdit,
  onDelete,
  dragHandleProps,
  isActive,
  onToggleActive,
  isReadOnly = false, // 🎯 Default a false
  canEdit = true,
  canDelete = true,
}: CardProps) => {
  const { t } = useTranslation('rulesModule');

  const handleClick = () => {
    if ((isReadOnly || canEdit) && onEdit) {
      onEdit();
    }
  };

  return (
    <div
      className={`py-4 border-b border-card-border hover:bg-muted/30 transition-colors flex items-center gap-4 ${className} ${isReadOnly || canEdit ? 'cursor-pointer' : ''}`}
      onClick={isReadOnly || canEdit ? handleClick : undefined}
    >
      {/* 🎯 Solo mostrar drag handle si tiene permiso de edición y NO es modo solo lectura */}
      {!isReadOnly && canEdit && (
        <div className="flex-shrink-0 flex items-center justify-center h-full" style={{ minWidth: 44, minHeight: 44 }}>
          {isMobile ? (
            <div
              className="p-2 rounded transition-all duration-200 text-muted-foreground hover:text-primary"
              style={{
                touchAction: 'manipulation',
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                userSelect: 'none',
              }}
              {...dragHandleProps}
            >
              <FontAwesomeIcon icon={faGripVertical} className="w-4 h-4" />
            </div>
          ) : (
            <DragHandle id={rules.id} {...dragHandleProps} />
          )}
        </div>
      )}
      {/* Info principal */}
      <div className="flex-1 min-w-0">
        <div className="font-medium mb-1.5 text-foreground">{rules.name}</div>
        <RuleListSummary rule={rules} className="mb-2" />
        <Badge
          variant="default"
          className={`mt-1 w-fit text-xs flex items-center gap-1 ${rules.is_active ? 'text-success bg-bg-success' : 'text-error bg-bg-error'}`}
        >
          {rules.is_active ? (
            <>
              <FontAwesomeIcon icon={faCheckCircle} className="w-3 h-3" />
              {t('table.active')}
            </>
          ) : (
            <>
              <FontAwesomeIcon icon={faCircleXmark} className="w-3 h-3" />
              {t('table.inactive')}
            </>
          )}
        </Badge>
      </div>
      {/* 🎯 Solo mostrar acciones si NO es modo solo lectura y tiene algún permiso */}
      {!isReadOnly && (canEdit || canDelete) && (
        <div className="flex-shrink-0 flex items-center">
          <DropdownActions
            onEdit={onEdit}
            onDelete={onDelete}
            isActive={isActive}
            onToggleActive={onToggleActive}
            canEdit={canEdit}
            canDelete={canDelete}
          />
        </div>
      )}
    </div>
  );
};
